import queryString from 'query-string';
import { APP_ID_USERPORTAL, APP_ID_BACKOFFICE, APP_ID_ADMIN } from 'shared/constants';

let devMode = process.env.NODE_ENV !== 'production';

export const APP_HOST_PORTAL = process.env.REACT_APP_HOST_PORTAL || 'localhost:3004';
export const APP_HOST_BACKOFFICE = process.env.REACT_APP_HOST_BACKOFFICE || 'localhost:3002';
export const APP_HOST_ADMIN = process.env.REACT_APP_HOST_ADMIN || 'localhost:3003';

if (APP_HOST_PORTAL.includes('host.docker.internal')
  || APP_HOST_BACKOFFICE.includes('host.docker.internal')
  || APP_HOST_ADMIN.includes('host.docker.internal')) {
  devMode = true;
}

export const BLOG_URL = process.env.REACT_APP_BLOG_URL || 'https://blog.skilleto.cz';
export const LINKEDIN_URL = 'https://www.linkedin.com/company/skilleto-cz';
export const FACEBOOK_URL = 'https://www.facebook.com/Skilleto.cz';

export const STATIC_BASE_URL = devMode ? '/_api' : `https://${APP_HOST_PORTAL.replace('www.', 'static.')}`;

const appIdByHost = {
  [APP_HOST_PORTAL]: APP_ID_USERPORTAL,
  [APP_HOST_BACKOFFICE]: APP_ID_BACKOFFICE,
  [APP_HOST_ADMIN]: APP_ID_ADMIN,
};

const proto = devMode ? 'http' : 'https';
const baseUrlByAppId = {
  [APP_ID_USERPORTAL]: `${proto}://${APP_HOST_PORTAL}`,
  [APP_ID_BACKOFFICE]: `${proto}://${APP_HOST_BACKOFFICE}`,
  [APP_ID_ADMIN]: `${proto}://${APP_HOST_ADMIN}`,
};

export const getBaseUrlByAppId = (appId, withoutProto = false) => {
  if (baseUrlByAppId[appId]) {
    if (withoutProto) {
      return baseUrlByAppId[appId].replace(`${proto}://`, '');
    }
    return baseUrlByAppId[appId];
  }

  throw new Error(`Invalid app id: ${appId}`);
};

export const getAppIdByHost = (host) => {
  if (appIdByHost[host]) {
    return appIdByHost[host];
  }

  throw new Error(`Invalid app host: ${host}`);
};

export const createRoute = (url, params = {}, query = {}, appId = null) => {
  const keys = Object.keys(params);
  const qs = queryString.stringify(query);
  const hostname = appId === null ? '' : getBaseUrlByAppId(appId);
  return hostname + keys.reduce((acc, curr) => acc.replace(`:${curr}`, params[curr]), url) + (qs ? `?${qs}` : qs);
};

export const createUserportalLink = (url, params = {}, query = {}) => createRoute(url, params, query, APP_ID_USERPORTAL);
export const createBackofficeLink = (url, params = {}, query = {}) => createRoute(url, params, query, APP_ID_BACKOFFICE);
export const createAdminLink = (url, params = {}, query = {}) => createRoute(url, params, query, APP_ID_ADMIN);
export const createStaticsLink = path => `${STATIC_BASE_URL}/${path.replace(/^\//, '')}`;

export const EDIT_DEFAULT_SUFFIX = '/edit';
export const EDIT_ROLES_SKILLS_SUFFIX = '/edit/roles-skills';
export const EDIT_BENEFITS_SUFFIX = '/edit/benefits';
export const EDIT_PREFERENCES_SUFFIX = '/edit/preferences';

export const invitationRoutePrefix = '/invitation/';
export const userportal = {
  root: '/',
  login: '/login',
  loginAsUser: '/login?as=user',
  signup: '/signup',
  signupAsUser: '/signup?as=user',
  invitation: `${invitationRoutePrefix}:code`,
  resetPassword: '/reset-password',
  resetPasswordLink: '/reset-password/:hash',
  about: '/about',
  howItWorks: '/how-it-works',
  contact: '/contact',
  contactForm: '/contact#form',
  tos: '/terms-of-service',
  gdpr: '/personal-data-gdpr',
  company: {
    detail: '/company/:slug',
  },
  job: {
    detail: '/jobs/:slug'
  },
  user: {
    favoriteJobs: '/user/favorite-jobs',
    applications: '/user/applications',
    application: '/user/applications/:jobSlug/:applicationId',
    offers: '/user/offers',
    offer: '/user/offers/:jobSlug/:offerId',
    settings: '/user/settings',
    profile: {
      view: '/user/profile',
      editInfo: '/user/profile/edit-info',
      editRolesSkills: '/user/profile/edit-roles-skills',
      editLanguages: '/user/profile/edit-languages',
      editWork: '/user/profile/work/:workId',
      editEducation: '/user/profile/education/:educationId',
      editPreferences: '/user/profile/edit-preferences',
      editAttachment: '/user/profile/attachments/:attachmentId',
      editLink: '/user/profile/links/:linkId',
      imports: '/user/profile/imports',
      accountDeletion: '/user/profile/account-deletion',
      accountDeletionConfirm: '/user/profile/account-deletion/confirm/:token',
    }
  },
  oauth: {
    linkedIn: {
      login: '/oauth/linkedin',
      callbackUrl: '/oauth/linkedin/callback'
    },
    google: {
      login: '/oauth/google',
      callbackUrl: '/oauth/google/callback'
    },
    signup: '/oauth/signup'
  }
};

export const backoffice = {
  root: '/',
  rootAlias: '/home',
  rootHash: {
    howItWorks: '#howitworks',
    why: '#why',
    pricing: '#pricing',
    contact: '#contact'
  },
  login: '/login',
  switchAccount: '/switch-account',
  signup: '/signup',
  about: '/about',
  contact: '/contact',
  tos: '/terms-and-conditions',
  pricing: '/pricing',
  gdpr: '/personal-data-gdpr',
  howItWorks: '/how-it-works',
  invitation: `${invitationRoutePrefix}:code`,
  resetPassword: '/reset-password',
  resetPasswordLink: '/reset-password/:hash',
  dashboard: '/dashboard',
  user: {
    profile: {
      view: '/user-profile',
      edit: '/user-profile/edit'
    },
    settings: '/user-settings'
  },
  company: {
    profile: {
      view: '/company-profile',
      edit: '/company-profile/edit',
      headerImageSettings: '/company-profile/header-image-settings',
      editModule: '/company-profile/module/:moduleId/edit',
      newModule: '/company-profile/module/:type/new'
    },
    users: {
      list: '/users'
    },
    ogPreview: '/company/__og/:slug',
  },
  job: {
    list: '/jobs',
    archive: '/archived-jobs',
    add: '/jobs/new/edit',
    import: '/jobs/new/import',
    detail: '/jobs/:jobId',
    applications: '/jobs/:jobId/applications',
    edit: `/jobs/:jobId${EDIT_DEFAULT_SUFFIX}`,
    editRolesSkills: `/jobs/:jobId${EDIT_ROLES_SKILLS_SUFFIX}`,
    editBenefits: `/jobs/:jobId${EDIT_BENEFITS_SUFFIX}`,
    ogPreview: '/jobs/__og/:slug',
  },
  applicant: {
    list: '/applicants',
    detail: '/applicants/:applicationId',
  },
  candidate: {
    search: '/candidates/search',
    list: '/candidates/list',
    detail: '/candidates/:userId',
  },
  b2b: {
    specializations: {
      index: '/b2b/specializations',
      detail: '/b2b/specializations/:userId',
      edit: `/b2b/specializations/:userId${EDIT_DEFAULT_SUFFIX}`,
      editRolesSkills: `/b2b/specializations/:userId${EDIT_ROLES_SKILLS_SUFFIX}`,
      editPreferences: `/b2b/specializations/:userId${EDIT_PREFERENCES_SUFFIX}`,
    },
    jobs: {
      index: '/b2b/jobs',
      detail: '/b2b/jobs/:slug',
    }
  },
  subscriptions: {
    list: '/subscriptions',
    order: {
      new: '/subscriptions/new-order',
      newOverview: '/subscriptions/new-order/overview',
      detail: '/subscriptions/order/:orderId'
    }
  },
  lead: {
    list: '/leads',
  },
  integrations: {
    list: '/integrations',
    edit: '/integrations/:id',
    logs: '/integrations/:id/logs',
  },
  importedCandidates: {
    list: '/imported-candidates',
    candidateDetail: '/imported-candidates/:userId',
    importHistory: '/imported-candidates/imports',
    importNew: '/imported-candidates/imports/new',
    jobDetail: '/imported-candidates/imports/:jobId',
  }
};

export const admin = {
  root: '/',
  login: '/login',
  companies: {
    list: '/companies',
    signup: '/companies/signup/:invitationId',
    detail: '/companies/detail/:companyId',
    newInvitation: '/company-new-invitation',
  },
  users: {
    list: '/users',
    signup: '/users/signup/:invitationId',
    detail: '/users/detail/:userId',
    lastLogins: '/user-last-logins',
    newInvitation: '/user-new-invitation',
  },
  benefits: {
    list: '/benefits',
    detail: '/benefits/:benefitId'
  },
  skills: {
    list: '/skills',
    detail: '/skills/:skillId'
  },
  skillExtractorLogs: {
    list: '/skill-extractor-logs',
    detail: '/skill-extractor-logs/:code'
  },
  roles: {
    list: '/roles',
    detail: '/roles/:roleId'
  },
  orders: {
    list: '/orders',
    detail: '/orders/:orderId'
  },
  subscriptions: {
    list: '/subscriptions',
    detail: '/subscriptions/:subscriptionId',
    plans: '/subscription-plans',
    planDetail: '/subscription-plans/:planId'
  },
  invoices: {
    list: '/invoices',
    detail: '/invoices/:invoiceNumber'
  },
  invitations: {
    list: '/invitations',
    detail: '/invitations/:invitationId',
  },
  jobSearch: {
    list: '/job-search',
  },
  jobApplications: {
    list: '/job-applications',
    detail: '/job-applications/detail/:id',
    unread: '/job-applications/unread',
    aiSummaries: '/job-application-ai-summaries',
    aiSummaryDetail: '/job-application-ai-summaries/:id',
    followUps: '/job-application-follow-ups',
    followUpDetail: '/job-application-follow-ups/:id',
    rejectionMessages: '/job-application-rejection-messages',
    rejectionMessageDetail: '/job-application-rejection-messages/:id',
  },
  jobOffers: {
    list: '/job-offers',
    detail: '/job-offers/detail/:id',
    offerMessages: '/candidate-offer-messages',
    offerMessageDetail: '/candidate-offer-messages/:id',
  },
  aiRecruiter: {
    activations: '/ai-recruiter',
    newActivation: '/ai-recruiter/activations/new',
    activationDetail: '/ai-recruiter/activations/:id',
    offers: '/ai-recruiter/offers',
    offerDetail: '/ai-recruiter/offers/:offerId',
    items: '/ai-recruiter/items',
    itemDetail: '/ai-recruiter/items/:itemId',
    runs: '/ai-recruiter/runs',
    runDetail: '/ai-recruiter/runs/:runId',
  },
  parses: {
    list: '/parses',
    detail: '/parses/:parseId'
  },
  recruiterReviewAnalysis: {
    list: '/recruiter-review-analysis',
    detail: '/recruiter-review-analysis/:id'
  },
  feedback: {
    list: '/feedback',
    detail: '/feedback/:id'
  },
  leads: {
    list: '/leads',
  },
  leadReviews: {
    list: '/lead-review-requests',
    detail: '/lead-review-requests/:id',
  },
  jobImports: {
    list: '/job-imports',
    detail: '/job-imports/:id'
  },
  scoringEmbeddingsRuns: {
    list: '/scoring-embeddings-runs',
    detail: '/scoring-embeddings-runs/:runId'
  },
  jobEmbeddings: {
    list: '/job-embeddings',
    detail: '/job-embeddings/:jobId'
  },
  userProfileEmbeddings: {
    list: '/user-profile-embeddings',
    detail: '/user-profile-embeddings/:userProfileId'
  },
  stats: '/stats',
  salaryStats: '/salary-stats'
};

export default {
  APP_HOST_PORTAL,
  APP_HOST_BACKOFFICE,
  APP_HOST_ADMIN,
  BLOG_URL,
  LINKEDIN_URL,
  FACEBOOK_URL,
  STATIC_BASE_URL,
  EDIT_DEFAULT_SUFFIX,
  EDIT_ROLES_SKILLS_SUFFIX,
  EDIT_BENEFITS_SUFFIX,
  EDIT_PREFERENCES_SUFFIX,
  getBaseUrlByAppId,
  getAppIdByHost,
  createUserportalLink,
  createBackofficeLink,
  createAdminLink,
  createStaticsLink,
  createRoute,
  invitationRoutePrefix,
  userportal,
  backoffice,
  admin,
};
